import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Hero from "@/components/sections/Hero";
import ProductCard from "@/components/ProductCard";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { Reveal, Stagger } from "@/components/Reveal";
import {
    WhySweetPearls, BuildBoxTeaser, ProductStory, CateringTeaser, SocialProof, Newsletter,
} from "@/components/sections/HomeSections";

function SkeletonCard() {
    return (
        <div className="rounded-3xl border border-caramel/10 bg-white p-4 pt-16">
            <div className="mx-auto -mt-12 mb-6 h-32 w-32 rounded-full skeleton" />
            <div className="mx-auto h-4 w-24 rounded skeleton" />
            <div className="mx-auto mt-3 h-6 w-40 rounded skeleton" />
            <div className="mx-auto mt-3 h-10 w-full rounded skeleton" />
        </div>
    );
}

export default function Home() {
    const { data: products, isLoading } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await api.get("/products")).data,
    });
    const { data: reviews } = useQuery({
        queryKey: ["reviews"],
        queryFn: async () => (await api.get("/reviews")).data,
    });

    return (
        <div>
            <Hero />

            {/* Featured Flavors */}
            <section className="mx-auto max-w-7xl px-5 pt-24 lg:px-8">
                <Reveal className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Featured Flavors</p>
                        <h2 className="mt-2 font-serif text-3xl font-semibold text-roast sm:text-4xl lg:text-5xl">
                            Our most-loved <span className="italic text-caramel">creations</span>
                        </h2>
                    </div>
                    <Link to="/shop" className="inline-flex items-center gap-2 font-semibold text-caramel hover:underline">
                        View all flavors <ArrowRight size={18} />
                    </Link>
                </Reveal>

                <Stagger className="mt-20 grid grid-cols-1 gap-x-6 gap-y-20 sm:grid-cols-2 lg:grid-cols-3">
                    {isLoading
                        ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
                        : products?.slice(0, 6).map((p) => <ProductCard key={p.id} product={p} />)}
                </Stagger>
            </section>

            <WhySweetPearls />
            <BuildBoxTeaser />
            <ProductStory />

            {/* Reviews */}
            <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
                <Reveal className="mb-14 text-center">
                    <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Customer Love</p>
                    <h2 className="mt-2 font-serif text-3xl font-semibold text-roast sm:text-4xl lg:text-5xl">Sweet words from sweet people</h2>
                </Reveal>
                <ReviewsCarousel reviews={reviews || []} />
            </section>

            <CateringTeaser />
            <SocialProof />
            <Newsletter />
        </div>
    );
}
